import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/Dialog';
import { useIsMobile } from '@/hooks/use-mobile';
import type { Coach } from '@court-sync/shared';
import type { LessonOccurrenceClickPayload } from '@/utils/scheduleOccurrences';
import type { LessonOccurrence } from './LessonCalendarView';
import type { LessonAvailabilityWarningDays } from '@/utils/coachAvailabilityWarnings';
import type { LessonCalendarTooltipContext } from '@/utils/lessonCalendarTooltip';
import { LessonCalendarDayPopoverContent } from './LessonCalendarDayPopoverContent';

interface LessonCalendarDayPopoverProps {
  day: Date;
  occurrences: LessonOccurrence[];
  getDotChipProps: (occ: LessonOccurrence) => { className: string; style?: React.CSSProperties };
  onLessonOccurrenceClick?: (payload: LessonOccurrenceClickPayload) => void;
  onScheduleOccurrenceClick?: (occurrence: LessonOccurrence) => void;
  coaches?: Coach[];
  coachAvailabilityWarnings?: LessonAvailabilityWarningDays;
  hasCoach: (lesson: LessonOccurrence['lesson']) => boolean;
  tooltipContext?: LessonCalendarTooltipContext;
  children: React.ReactElement;
}

export function LessonCalendarDayPopover({
  day,
  occurrences,
  getDotChipProps,
  onLessonOccurrenceClick,
  onScheduleOccurrenceClick,
  coaches = [],
  coachAvailabilityWarnings = new Map(),
  hasCoach,
  tooltipContext,
  children,
}: LessonCalendarDayPopoverProps) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const title = format(day, 'EEEE, MMMM d, yyyy');

  const content = (
    <LessonCalendarDayPopoverContent
      day={day}
      occurrences={occurrences}
      getDotChipProps={getDotChipProps}
      onLessonOccurrenceClick={onLessonOccurrenceClick}
      onScheduleOccurrenceClick={onScheduleOccurrenceClick}
      onClose={() => setOpen(false)}
      coaches={coaches}
      coachAvailabilityWarnings={coachAvailabilityWarnings}
      hasCoach={hasCoach}
      tooltipContext={tooltipContext}
      listMaxHeightClassName={
        isMobile ? 'max-h-[min(28rem,65vh)]' : 'max-h-[min(16rem,50vh)]'
      }
    />
  );

  if (isMobile) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{children}</DialogTrigger>
        <DialogContent
          className="w-[min(24rem,calc(100vw-2rem))] max-w-none gap-0 overflow-hidden rounded-xl border border-border bg-popover p-0 shadow-xl [&>button.absolute]:hidden"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <DialogTitle className="sr-only">{title}</DialogTitle>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="w-[min(18rem,calc(100vw-2rem))] rounded-xl border border-border bg-popover p-0 shadow-lg"
        align="start"
        side="bottom"
        sideOffset={4}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {content}
      </PopoverContent>
    </Popover>
  );
}

export function lessonCalendarDayPopoverAriaLabel(day: Date, lessonCount?: number): string {
  const dateLabel = format(day, 'MMMM d, yyyy');
  if (lessonCount != null && lessonCount > 0) {
    return `View ${lessonCount} lesson${lessonCount === 1 ? '' : 's'} on ${dateLabel}`;
  }
  return `View lessons on ${dateLabel}`;
}
